import { Constructor } from '@angular/cdk/table';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

type WithProcessingType = {
  isProcessing: boolean;
  processingMsg: string;
  emptyResultMsg: string;
  process$<T>(
    observableToProcess$: Observable<T>,
    {
      processingKey,
      processingMsg,
      emptyResultMsg,
    }?: {
      processingKey?: string;
      processingMsg?: string;
      emptyResultMsg?: string;
    },
  ): Observable<T>;
};

export function WithProcessing<TBase extends Constructor<any>>(
  baseClass: TBase,
): {
  new (...args: any[]): WithProcessingType;
  prototype: WithProcessingType;
} & TBase {
  return class Processable extends baseClass {
    protected isProcessing = false;
    protected processingMsg!: string;
    protected emptyResultMsg!: string;

    private readonly PROCESSING_DEFAULT_KEY = 'isProcessing';

    constructor(...args: any[]) {
      super(...args);
    }

    protected process$<T>(
      observableToProcess$: Observable<T>,
      {
        processingKey,
        processingMsg,
        emptyResultMsg,
      }: {
        processingKey?: string;
        processingMsg?: string;
        emptyResultMsg?: string;
      } = {},
    ): Observable<T> {
      processingKey = processingKey ?? this.PROCESSING_DEFAULT_KEY;
      this._setProcessingVariables({
        processing: { key: processingKey, value: true },
        processingMsg: processingMsg ?? `Fetching...`,
        emptyResultMsg: emptyResultMsg ?? `Could not retrieve results.`,
      });
      return observableToProcess$.pipe(
        finalize(() => {
          this[processingKey ?? this.PROCESSING_DEFAULT_KEY] = false;
        }),
      );
    }

    private _setProcessingVariables({
      processing,
      processingMsg,
      emptyResultMsg,
    }: {
      processing: { key: string; value: boolean };
      processingMsg: string;
      emptyResultMsg: string;
    }): void {
      this[processing.key] = processing.value;
      this.processingMsg = processingMsg;
      this.emptyResultMsg = emptyResultMsg;
    }
  };
}
